import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import useSEO from "../../../hooks/useSEO";

const ProgressiveWebApp = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // SEO dinámico
  useSEO({
    title: "Progressive Web App (PWA) - Apps Web Ultrarrápidas | SEOgenia",
    description: "Convertimos tu web en una PWA ultrarrápida que funciona offline, se instala como app nativa, y carga en menos de 1 segundo. Experiencia de app con alcance de web.",
    keywords: "progressive web app, pwa, app web, offline first, service worker, web app manifest",
    canonical: "https://seogenia.es/servicios/progressive-web-app",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Progressive Web App (PWA)",
      "description": "Desarrollo de Progressive Web Apps ultrarrápidas con funcionalidad offline",
      "provider": {
        "@type": "Organization",
        "name": "SEOgenia",
        "url": "https://seogenia.es"
      },
      "areaServed": "ES",
      "serviceType": "Desarrollo PWA"
    }
  });

  const faqs = [
    {
      question: "¿Qué es exactamente una Progressive Web App (PWA)?",
      answer: "Una PWA es una web que se comporta como una app nativa: se puede instalar en el dispositivo sin app store, funciona offline o con conexión lenta, envía notificaciones push, carga instantáneamente, y tiene icono en la pantalla de inicio. Combina lo mejor de ambos mundos: el alcance y SEO de una web con la experiencia y funcionalidad de una app móvil nativa."
    },
    {
      question: "¿Cuánto cuesta desarrollar una PWA comparado con apps nativas?",
      answer: "Una PWA cuesta 60-70% menos que desarrollar apps nativas para iOS y Android por separado. Con una PWA tienes un solo código base que funciona en todos los dispositivos y navegadores. No pagas comisiones de app stores (30% en Apple/Google), no necesitas aprobaciones para actualizaciones, y el mantenimiento es mucho más simple. El ROI es significativamente superior."
    },
    {
      question: "¿Las PWAs funcionan en iPhone/iOS?",
      answer: "Sí, desde iOS 11.3 (2018) Safari soporta PWAs. Puedes instalar PWAs en iPhone/iPad, funcionan offline, y tienen icono en pantalla de inicio. Sin embargo, iOS tiene algunas limitaciones vs Android: no soporta notificaciones push (aún), tiene límites de almacenamiento más estrictos, y algunas APIs avanzadas no están disponibles. Aun así, el 90% de funcionalidades PWA funcionan perfectamente en iOS."
    },
    {
      question: "¿Una PWA mejora mi posicionamiento en Google?",
      answer: "Sí, indirectamente pero significativamente. Las PWAs mejoran Core Web Vitals (velocidad, interactividad, estabilidad visual), reducen tasa de rebote (mejor experiencia = usuarios se quedan más), aumentan tiempo en sitio y páginas por sesión, y mejoran conversiones móviles. Google premia estos factores de experiencia de usuario con mejor ranking. Clientes típicos ven mejoras de 10-25% en rankings móviles tras implementar PWA."
    },
    {
      question: "¿Puedo convertir mi web actual en PWA o necesito reconstruir desde cero?",
      answer: "En la mayoría de casos podemos convertir tu web existente en PWA sin reconstruir desde cero. Añadimos Service Worker (para funcionalidad offline), Web App Manifest (para instalación), optimizamos assets para carga rápida, e implementamos estrategias de caché inteligente. Si tu web está muy desactualizada o tiene problemas de rendimiento graves, puede ser más eficiente reconstruir con tecnología moderna (React, Next.js, etc.)."
    },
    {
      question: "¿Las PWAs funcionan completamente offline?",
      answer: "Depende de cómo las configuremos. Podemos implementar diferentes estrategias: Offline First (todo funciona offline, sincroniza cuando hay conexión), Cache First (usa caché si está disponible, red como fallback), Network First (intenta red primero, caché como fallback), o híbrido. Para contenido estático (páginas, imágenes, estilos) offline completo es fácil. Para contenido dinámico (datos en tiempo real) usamos estrategias de sincronización inteligente."
    },
    {
      question: "¿Necesito publicar mi PWA en Google Play o App Store?",
      answer: "No es necesario, pero es opcional. Las PWAs se instalan directamente desde tu web sin app stores. Sin embargo, puedes publicar tu PWA en Google Play (fácil, con TWA - Trusted Web Activities) y Microsoft Store (también soporta PWAs). Apple App Store no acepta PWAs puras, pero puedes usar wrappers como Capacitor. La ventaja de app stores es mayor descubribilidad; la desventaja son comisiones y procesos de aprobación."
    },
    {
      question: "¿Cuánto tiempo tarda en desarrollarse una PWA?",
      answer: "Para convertir una web existente en PWA: 2-4 semanas (añadir Service Worker, manifest, optimizaciones). Para desarrollar una PWA desde cero: 6-12 semanas dependiendo de complejidad (e-commerce complejo toma más que un blog). Para PWAs muy avanzadas con funcionalidades nativas complejas: 3-6 meses. Proporcionamos roadmap detallado en la fase de planificación con hitos y entregas incrementales."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-900"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full shadow-sm mb-6">
              <i className="ri-smartphone-line text-purple-400"></i>
              <span className="text-sm font-medium text-white">Tecnología Avanzada</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Progressive Web App (PWA)
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Convierte tu web en una aplicación móvil ultrarrápida, instalable y que funciona offline. La experiencia de una app nativa con el alcance de la web.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#contacto"
                className="px-8 py-3 bg-purple-600 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                Convertir a PWA
              </a>
              <a
                href="#beneficios"
                className="px-8 py-3 bg-white/10 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 border border-white/20 whitespace-nowrap"
              >
                Ver Ventajas
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is PWA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                ¿Qué es una Progressive Web App?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Una PWA es una web que se comporta como una aplicación nativa. Se puede instalar en el móvil, funciona offline, envía notificaciones push y carga instantáneamente. Todo sin pasar por App Store o Google Play.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Google prioriza las PWAs en sus rankings porque ofrecen la mejor experiencia de usuario posible: rápidas, confiables y atractivas.
              </p>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-rocket-line text-purple-600 mr-2"></i>
                  Impacto Real
                </h3>
                <p className="text-gray-700">
                  Las PWAs aumentan la conversión hasta un <strong>52%</strong> y reducen el tiempo de carga en un <strong>63%</strong> según estudios de Google.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/e89dd557ffb3ddaaa1c151ef9bdc5794.jpg"
                alt="Progressive Web App"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ventajas de una PWA
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Por qué tu negocio necesita una Progressive Web App
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-download-cloud-line",
                title: "Instalable",
                description: "Los usuarios pueden instalarla en su pantalla de inicio sin pasar por tiendas de apps",
                color: "cyan"
              },
              {
                icon: "ri-wifi-off-line",
                title: "Funciona Offline",
                description: "Sigue funcionando sin conexión gracias a Service Workers y caché inteligente",
                color: "purple"
              },
              {
                icon: "ri-flashlight-line",
                title: "Carga Instantánea",
                description: "Tiempos de carga menores a 1 segundo con caché y precarga estratégica",
                color: "pink"
              },
              {
                icon: "ri-notification-3-line",
                title: "Push Notifications",
                description: "Envía notificaciones push para recuperar usuarios y aumentar engagement",
                color: "cyan"
              },
              {
                icon: "ri-smartphone-line",
                title: "Experiencia App",
                description: "Pantalla completa, sin barra de navegador, gestos nativos y animaciones fluidas",
                color: "purple"
              },
              {
                icon: "ri-search-eye-line",
                title: "Mejor SEO",
                description: "Google prioriza PWAs en rankings móviles por su excelente experiencia de usuario",
                color: "pink"
              },
              {
                icon: "ri-money-dollar-circle-line",
                title: "Menor Costo",
                description: "Una sola PWA funciona en iOS, Android y web. Sin desarrollos nativos separados",
                color: "cyan"
              },
              {
                icon: "ri-refresh-line",
                title: "Actualizaciones Automáticas",
                description: "Se actualiza automáticamente sin que el usuario tenga que hacer nada",
                color: "purple"
              },
              {
                icon: "ri-shield-check-line",
                title: "Segura (HTTPS)",
                description: "Requiere HTTPS garantizando seguridad total en todas las comunicaciones",
                color: "pink"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 bg-gradient-to-br from-${benefit.color}-500 to-${benefit.color}-600 rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Características Técnicas que Implementamos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Tecnologías de vanguardia para una PWA de alto rendimiento
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Service Workers",
                description: "Scripts que funcionan en segundo plano para:",
                points: [
                  "Caché inteligente de recursos críticos",
                  "Funcionamiento offline completo",
                  "Sincronización en background",
                  "Interceptación de peticiones de red",
                  "Actualización automática de contenido"
                ],
                icon: "ri-settings-3-line"
              },
              {
                title: "Web App Manifest",
                description: "Archivo JSON que define la apariencia de la app:",
                points: [
                  "Nombre, iconos y colores de marca",
                  "Orientación de pantalla (portrait/landscape)",
                  "Modo de visualización (standalone/fullscreen)",
                  "URL de inicio y scope de la app",
                  "Splash screen personalizada"
                ],
                icon: "ri-file-code-line"
              },
              {
                title: "App Shell Architecture",
                description: "Estructura que carga la interfaz instantáneamente:",
                points: [
                  "HTML, CSS y JS mínimos cacheados",
                  "Carga de contenido dinámico posterior",
                  "Navegación instantánea entre páginas",
                  "Skeleton screens mientras carga",
                  "Transiciones suaves y naturales"
                ],
                icon: "ri-layout-line"
              },
              {
                title: "Push Notifications",
                description: "Sistema completo de notificaciones push:",
                points: [
                  "Integración con Firebase Cloud Messaging",
                  "Notificaciones personalizadas por usuario",
                  "Segmentación y targeting avanzado",
                  "Analytics de apertura y conversión",
                  "Rich notifications con imágenes y acciones"
                ],
                icon: "ri-notification-badge-line"
              },
              {
                title: "Optimización de Rendimiento",
                description: "Técnicas avanzadas para máxima velocidad:",
                points: [
                  "Code splitting y lazy loading",
                  "Compresión Brotli y Gzip",
                  "Minificación de assets",
                  "Critical CSS inline",
                  "Preload y prefetch estratégico"
                ],
                icon: "ri-speed-up-line"
              },
              {
                title: "Responsive y Adaptive",
                description: "Diseño que se adapta perfectamente a cualquier dispositivo:",
                points: [
                  "Mobile-first design approach",
                  "Breakpoints optimizados",
                  "Imágenes responsive con srcset",
                  "Touch gestures nativos",
                  "Viewport meta tags correctos"
                ],
                icon: "ri-device-line"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 mb-4">{feature.description}</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {feature.points.map((point, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-green-500 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-600 text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Proceso de Conversión a PWA
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transformamos tu web en una Progressive Web App en 5 pasos
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-6">
            {[
              {
                step: "1",
                title: "Auditoría",
                description: "Analizamos tu web actual y definimos requisitos",
                icon: "ri-search-line"
              },
              {
                step: "2",
                title: "HTTPS",
                description: "Implementamos certificado SSL si no lo tienes",
                icon: "ri-shield-check-line"
              },
              {
                step: "3",
                title: "Service Worker",
                description: "Desarrollamos estrategia de caché y offline",
                icon: "ri-settings-3-line"
              },
              {
                step: "4",
                title: "Manifest",
                description: "Creamos manifest con iconos y configuración",
                icon: "ri-file-code-line"
              },
              {
                step: "5",
                title: "Testing",
                description: "Validamos con Lighthouse y dispositivos reales",
                icon: "ri-checkbox-circle-line"
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white p-6 rounded-2xl shadow-lg h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <i className={`${process.icon} text-xl text-white`}></i>
                  </div>
                  <div className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-pink-600 bg-clip-text text-transparent mb-2 text-center">
                    {process.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">{process.title}</h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">{process.description}</p>
                </div>
                {index < 4 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-xl text-gray-300"></i>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Casos de Éxito Reales
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Empresas que transformaron su negocio con PWAs
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                company: "Twitter Lite",
                results: [
                  "65% aumento en páginas por sesión",
                  "75% aumento en tweets enviados",
                  "20% reducción en tasa de rebote"
                ],
                color: "cyan"
              },
              {
                company: "Pinterest",
                results: [
                  "60% aumento en engagement",
                  "44% aumento en ingresos publicitarios",
                  "40% más tiempo en el sitio"
                ],
                color: "pink"
              },
              {
                company: "Starbucks",
                results: [
                  "2x usuarios activos diarios",
                  "99.84% menor tamaño que app iOS",
                  "Funciona offline completamente"
                ],
                color: "green"
              }
            ].map((story, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-8 rounded-2xl border border-gray-700"
              >
                <div className={`w-full h-2 bg-${story.color}-500 rounded-full mb-6`}></div>
                <h3 className="text-2xl font-bold text-white mb-6">{story.company}</h3>
                <ul className="space-y-3">
                  {story.results.map((result, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-arrow-right-up-line text-green-400 text-xl mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-200 font-medium">{result}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-gray-600">
              Resolvemos tus dudas sobre Progressive Web Apps
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-emerald-600 flex-shrink-0 transition-transform duration-300 ${
                      openFAQ === index ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFAQ === index ? "max-h-[600px]" : "max-h-0"
                  }`}
                >
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Convierte tu Web en una PWA de Alto Rendimiento
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Ofrece la mejor experiencia de usuario y mejora tu SEO con una Progressive Web App
            </p>
            <Link
              to="/#contacto"
              className="inline-block px-8 py-4 bg-white text-gray-900 rounded-full font-bold hover:shadow-2xl transition-all duration-300 whitespace-nowrap"
            >
              Solicitar Conversión a PWA
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para convertir tu web en una PWA?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Ofrece una experiencia de app nativa sin necesidad de descargas
          </p>
          <Link 
            to="/#contacto"
            className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-full font-medium text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            Solicitar Consulta Gratuita
          </Link>
        </div>
      </section>

      {/* Servicios Relacionados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Servicios Complementarios
            </h2>
            <p className="text-xl text-gray-600">
              Potencia tu SEO con estos servicios relacionados
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Optimización Extrema */}
            <Link 
              to="/servicios/optimizacion-extrema"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-flashlight-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Optimización Extrema
              </h3>
              <p className="text-gray-600 mb-4">
                Combina tu PWA con optimizaciones extremas de velocidad para alcanzar puntuaciones perfectas en Core Web Vitals.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Voice Search SEO */}
            <Link 
              to="/servicios/voice-search-seo"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-mic-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Voice Search SEO
              </h3>
              <p className="text-gray-600 mb-4">
                Las PWAs son ideales para búsquedas por voz en móviles. Optimiza tu app para consultas conversacionales.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Schema Markup */}
            <Link 
              to="/servicios/schema-markup-avanzado"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-code-s-slash-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Schema Markup Avanzado
              </h3>
              <p className="text-gray-600 mb-4">
                Añade MobileApplication Schema a tu PWA para que Google la reconozca como una aplicación instalable.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProgressiveWebApp;